import React, {
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './profile.module.css';

// Components
import PasswordInput from '../password-input/password-input';
import PasswordRequirements from '../password-requirements/password-requirements';

// Models
import ApiError from '../../models/api-error';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import GeneralService from '../../services/general-service';
import StorageService from '../../services/storage-service';
import { validateEmail, validatePassword } from '../../helpers/validate-helper';
import { getErrorMessage } from '../../helpers/api-error-helper';

const Profile: FC<{ employee: Employee }> = ({ employee }) => {
  const generalService = new GeneralService();
  const storageService = new StorageService();
  const { setModalContent, setToastContent } = useContext(GeneralContext);

  const renderProfileEditItem = (
    field: string,
    clickChangeCallback: (...args) => any,
    value?: string
  ) => {
    return (
      <div className={styles.editItem}>
        <div className={styles.editItemTitle}>
          <p>{field}</p>
        </div>
        {value && (
          <div className={styles.editItemValue}>
            <p>{value}</p>
          </div>
        )}
        <div className={styles.editItemClickChange}>
          <p onClick={clickChangeCallback}>{`alterar ${field}`}</p>
        </div>
      </div>
    );
  };

  const [nameData, setNameData] = useState<{ newName: string }>({
    newName: employee.name,
  });
  const prevNameRef = useRef(nameData);
  const handleNameChangeClick = useCallback(() => {
    const resetPromise = () => {
      return new Promise<void>((resolve) => {
        setNameData({ newName: employee.name });
        resolve();
      });
    };

    const submitPromise = (submitData: { newName: string }) => {
      if (!submitData.newName)
        return Promise.reject('Preencha todos os campos');

      return generalService
        .updateProfileItem('name', submitData.newName)
        .then((newProfile) => {
          const authInfo = Object.assign(storageService.getAuthInfo(), {
            employee: newProfile,
          });
          storageService.setAuthInfo(authInfo);

          setToastContent({
            isOpen: true,
            text: 'Nome alterado!',
            type: 'success',
          });
        })
        .catch(() => 'Houve um erro ao alterar o nome.');
    };

    setModalContent({
      title: 'Alterar nome',
      modalContent: (
        <input
          type='text'
          name='name'
          id='name'
          placeholder='Digite o novo nome'
          defaultValue={employee.name}
          onChange={(evt) => setNameData({ newName: evt.target.value })}
        />
      ),
      submitButtonTitle: 'salvar',
      submitButtonType: 'primary',
      submitData: nameData,
      onSubmitButtonClick: submitPromise,
      onCloseModal: resetPromise,
      isOpen: true,
    });
  }, [
    nameData,
    setToastContent,
    setModalContent,
    generalService,
    storageService,
    employee,
  ]);
  useEffect(() => {
    if (
      nameData.newName !== employee.name &&
      prevNameRef.current.newName !== nameData.newName
    ) {
      handleNameChangeClick();
      prevNameRef.current = nameData;
    }
  }, [handleNameChangeClick, nameData, nameData.newName, employee.name]);

  const [emailData, setEmailData] = useState<{ newEmail: string }>({
    newEmail: employee.email,
  });
  const prevEmailRef = useRef(emailData);
  const handleEmailChangeClick = useCallback(() => {
    const resetPromise = () => {
      return new Promise<void>((resolve) => {
        setEmailData({ newEmail: employee.email });
        resolve();
      });
    };

    const submitPromise = (submitData: { newEmail: string }) => {
      const emailValidate = validateEmail(submitData.newEmail);

      if (!submitData.newEmail)
        return Promise.reject('Preencha todos os campos');
      else if (submitData.newEmail && !emailValidate.isValid)
        return Promise.reject(emailValidate.message);

      return generalService
        .updateProfileItem('email', submitData.newEmail)
        .then((newProfile) => {
          const authInfo = Object.assign(storageService.getAuthInfo(), {
            employee: newProfile,
          });
          storageService.setAuthInfo(authInfo);

          setToastContent({
            isOpen: true,
            text: 'Email alterado!',
            type: 'success',
          });
        })
        .catch(() => 'Houve um erro ao alterar o email.');
    };

    setModalContent({
      title: 'Alterar email',
      modalContent: (
        <input
          type='text'
          name='email'
          id='email'
          placeholder='Digite o novo email'
          defaultValue={employee.email}
          onChange={(evt) => setEmailData({ newEmail: evt.target.value })}
          autoCapitalize='none'
          autoCorrect='off'
        />
      ),
      submitButtonTitle: 'salvar',
      submitButtonType: 'primary',
      submitData: emailData,
      onSubmitButtonClick: submitPromise,
      onCloseModal: resetPromise,
      isOpen: true,
    });
  }, [
    emailData,
    setToastContent,
    setModalContent,
    generalService,
    storageService,
    employee,
  ]);
  useEffect(() => {
    if (
      emailData.newEmail !== employee.email &&
      prevEmailRef.current.newEmail !== emailData.newEmail
    ) {
      handleEmailChangeClick();
      prevEmailRef.current = emailData;
    }
  }, [handleEmailChangeClick, emailData, emailData.newEmail, employee.email]);

  const [passwordData, setPasswordData] = useState<{
    newPassword: string;
    confirmNewPassword: string;
  }>({ newPassword: null, confirmNewPassword: null });
  const prevPasswordDataRef = useRef(passwordData);
  const handlePasswordChangeClick = useCallback(() => {
    const resetPromise = () => {
      return new Promise<void>((resolve) => {
        setPasswordData({ newPassword: null, confirmNewPassword: null });
        resolve();
      });
    };

    const submitPromise = (submitData: {
      newPassword: string;
      confirmNewPassword: string;
    }) => {
      if (!submitData.newPassword || !submitData.confirmNewPassword) {
        return Promise.reject('Preencha todos os campos');
      } else if (submitData.newPassword !== submitData.confirmNewPassword) {
        return Promise.reject('A nova senha e a confirmação devem ser iguais.');
      } else if (!validatePassword(submitData.newPassword).isValid) {
        // The checklist names the unmet rule; this only backstops the disabled button.
        return Promise.reject('A senha não atende aos requisitos.');
      }

      return generalService
        .updateProfileItem('password', submitData.newPassword)
        .then((newProfile) => {
          const authInfo = Object.assign(storageService.getAuthInfo(), {
            employee: newProfile,
          });
          storageService.setAuthInfo(authInfo);

          setToastContent({
            isOpen: true,
            text: 'Senha alterada!',
            type: 'success',
          });
        })
        // Rejecting, not returning: a returned string resolves the promise, so Modal ran its
        // success path and closed as though the password had been changed.
        .catch((e: ApiError) =>
          Promise.reject(getErrorMessage(e, 'Houve um erro ao alterar a senha.'))
        );
    };

    const passwordsMatch =
      !!passwordData.newPassword &&
      passwordData.newPassword === passwordData.confirmNewPassword;
    const mismatch = !!passwordData.confirmNewPassword && !passwordsMatch;

    setModalContent({
      title: 'Alterar senha',
      // Keys, not positions: this modal is rebuilt on every keystroke, and inserting the checklist
      // between the two fields shifts every later child. React would then reconcile by index,
      // remount the confirm input and wipe what had been typed into it.
      modalContent: (
        <Fragment>
          <label key='newPasswordLabel' htmlFor='newPassword'>
            nova senha
          </label>
          <PasswordInput
            key='newPassword'
            name='newPassword'
            id='newPassword'
            placeholder='Digite a nova senha'
            onChange={(evt) =>
              setPasswordData({
                ...passwordData,
                newPassword: evt.target.value,
              })
            }
          />
          <PasswordRequirements
            key='requirements'
            password={passwordData.newPassword}
          />
          <label key='confirmNewPasswordLabel' htmlFor='confirmNewPassword'>
            confirme a senha
          </label>
          <PasswordInput
            key='confirmNewPassword'
            name='confirmNewPassword'
            id='confirmNewPassword'
            placeholder='Confirme a nova senha'
            onChange={(evt) =>
              setPasswordData({
                ...passwordData,
                confirmNewPassword: evt.target.value,
              })
            }
          />
          {mismatch && (
            <p key='mismatch' className={styles.passwordMismatch}>
              As senhas não coincidem.
            </p>
          )}
        </Fragment>
      ),
      submitButtonTitle: 'salvar',
      submitButtonType: 'primary',
      submitButtonDisabled:
        !passwordsMatch || !validatePassword(passwordData.newPassword).isValid,
      submitData: passwordData,
      onSubmitButtonClick: submitPromise,
      onCloseModal: resetPromise,
      isOpen: true,
    });
  }, [
    passwordData,
    setToastContent,
    setModalContent,
    generalService,
    storageService,
  ]);
  useEffect(() => {
    // Either field, not just the first: the checklist and the mismatch note have to refresh when
    // only the confirmation is being typed. Both empty still means "do not rebuild", which is what
    // stops the reset on close from re-opening the modal.
    if (
      (passwordData.newPassword || passwordData.confirmNewPassword) &&
      (prevPasswordDataRef.current.newPassword !== passwordData.newPassword ||
        prevPasswordDataRef.current.confirmNewPassword !==
          passwordData.confirmNewPassword)
    ) {
      handlePasswordChangeClick();
      prevPasswordDataRef.current = passwordData;
    }
  }, [
    handlePasswordChangeClick,
    passwordData,
    passwordData.newPassword,
    passwordData.confirmNewPassword,
  ]);

  return (
    <div className={styles.profile}>
      <h2>Seu perfil</h2>

      {renderProfileEditItem('nome', handleNameChangeClick, employee.name)}
      {renderProfileEditItem('email', handleEmailChangeClick, employee.email)}
      {renderProfileEditItem('senha', handlePasswordChangeClick)}
    </div>
  );
};

export default Profile;
